import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAppDispatch } from 'app/hooks';
import {
  ahuBoardFormsApi,
  type ICatalogRow,
} from 'app/services/ahuBoardFormsApi';
import { catalogLabel } from './formUtils';

/**
 * Phase 6e1 — PaginatedSelectView port (§7.7b, shared
 * /static/mvc/view/pageinated_select.js + pageinated_select.tmpl, markup
 * faithful): root div carries the legacy `custom-select-with-pagination`
 * class (+`open`) so common.css draws the caret + underline chrome, hidden
 * native `<select name class>` (so host-level change handlers & the verify
 * harness see the legacy control), custom `.selected` div + `.options` ul,
 * 50/page infinite scroll (<10 px from bottom → next page), `hasNoneOption`
 * "- -" dropdown row, closed-state '-----' for null values (legacy
 * selectedText/getValue), off-page `getValue` resolution via the list-url
 * `?id=` lookup, document click-outside close.
 *
 * [NEG]#46 fixed by construction: outside-click handlers are per-instance
 * React effects — removing one instance never unbinds the others.
 *
 * PERF (6e1 verify 6e1-3): off-page item resolution is memoized in a
 * module-level cache keyed by mfg|path|ccid|id, with in-flight dedupe and
 * `subscribe:false` dispatches — N selects resolving the same value share
 * ONE request and remounts resolve synchronously with zero dispatches.
 */

const RESOLUTION_CACHE = new Map<string, ICatalogRow | null>();
const RESOLUTION_INFLIGHT = new Map<string, Promise<ICatalogRow | null>>();

/** `.custom-select-with-pagination ul.options { max-height: 200px }`
 * (common.css:32293) — mirrored here only to decide which side to open on. */
const MENU_MAX_HEIGHT = 200;

/** Anchor for the open menu, in the portal's own (zoomed) px. `off` = the
 * control has scrolled out of its panel, so the menu is hidden with it. */
interface MenuPos {
  top: number;
  left: number;
  width: number;
  off: boolean;
}

/** Nearest scrolling ancestor — the Data Bar panel that used to clip the menu
 * (`#forms_sections`, `#forms_ahu`), found by computed style rather than by id
 * so a panel that gains a scrollbar later is covered too. */
const scrollParent = (el: HTMLElement): HTMLElement | null => {
  for (let n = el.parentElement; n && n !== document.body; n = n.parentElement) {
    const oy = getComputedStyle(n).overflowY;
    if (oy === 'auto' || oy === 'scroll') return n;
  }
  return null;
};

/** Cumulative CSS `zoom` in effect on `el` — `currentCSSZoom` where the browser
 * has it, otherwise the product of the cascade's own `zoom` values. */
const cssZoom = (el: HTMLElement): number => {
  const native = (el as HTMLElement & { currentCSSZoom?: number }).currentCSSZoom;
  if (typeof native === 'number' && native > 0) return native;
  let z = 1;
  for (let n: HTMLElement | null = el; n; n = n.parentElement) {
    const v = parseFloat(getComputedStyle(n).zoom);
    if (v && v !== 1) z *= v;
  }
  return z;
};

interface PaginatedSelectProps {
  mfgId: number;
  /** CMS path segment, e.g. 'ahu/casing/metal_sheets'. */
  path: string;
  constructionClassId?: number | null;
  value: number | null | undefined;
  onChange: (id: number | null) => void;
  /** Hidden native select attrs (host handler contract). */
  selectName: string;
  selectClass?: string;
  /** "- -" option (everything except outerliner_id, §7.7b). */
  hasNoneOption?: boolean;
  /** Closed-state text when value is null — default '-----' (legacy
   * selectedText); the bulk panel passes 'Varies' for mixed values. */
  nullText?: string;
  /** Reports collection emptiness so the host can hide the row (§7.3.1). */
  onEmptyChange?: (empty: boolean) => void;
  /** Extra data-* stamped on the native select (int-wall material selects). */
  dataId?: number;
  dataPlane?: string;
  disabled?: boolean;
}

const PaginatedSelect = ({
  mfgId,
  path,
  constructionClassId,
  value,
  onChange,
  selectName,
  selectClass,
  hasNoneOption = true,
  nullText = '-----',
  onEmptyChange,
  dataId,
  dataPlane,
  disabled,
}: PaginatedSelectProps) => {
  const dispatch = useAppDispatch();
  const [rows, setRows] = useState<ICatalogRow[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [resolved, setResolved] = useState<ICatalogRow | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const loadingRef = useRef(false);
  const [menuPos, setMenuPos] = useState<MenuPos | null>(null);

  // Page loader (accumulating — legacy collection keeps all fetched pages).
  useEffect(() => {
    let cancelled = false;
    loadingRef.current = true;
    const sub = dispatch(
      ahuBoardFormsApi.endpoints.getCmsPaginated.initiate({
        mfgId,
        path,
        page,
        constructionClassId,
      })
    );
    sub
      .unwrap()
      .then((res) => {
        if (cancelled) return;
        setCount(res.count);
        setRows((prev) => {
          const seen = new Set(prev.map((r) => r.id));
          return [...prev, ...res.results.filter((r) => !seen.has(r.id))];
        });
        // Legacy hide-empty: `collection.length < 1` after fetchPage(1) —
        // results length, not the count field (verify 6e2a-10 Floor Deck).
        if (page === 1) onEmptyChange?.(res.results.length < 1);
      })
      .catch(() => {
        // Fetch failure leaves the legacy collection empty → row hidden.
        if (!cancelled && page === 1) onEmptyChange?.(true);
      })
      .finally(() => {
        loadingRef.current = false;
      });
    return () => {
      cancelled = true;
      sub.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mfgId, path, page, constructionClassId, dispatch]);

  // Reset when the catalog key changes (construction-class switch reloads).
  useEffect(() => {
    setRows([]);
    setPage(1);
    setResolved(null);
  }, [mfgId, path, constructionClassId]);

  // Off-page value resolution (getValue → collection.getById) — module-level
  // memo + in-flight dedupe (PERF note above).
  useEffect(() => {
    if (value == null) {
      setResolved(null);
      return undefined;
    }
    if (rows.some((r) => r.id === value)) return undefined;
    const key = `${mfgId}|${path}|${constructionClassId ?? ''}|${value}`;
    const cached = RESOLUTION_CACHE.get(key);
    if (cached !== undefined) {
      setResolved(cached);
      return undefined;
    }
    let cancelled = false;
    let promise = RESOLUTION_INFLIGHT.get(key);
    if (!promise) {
      promise = dispatch(
        ahuBoardFormsApi.endpoints.getCmsCatalogItem.initiate(
          { mfgId, path, id: value, constructionClassId },
          { subscribe: false }
        )
      )
        .unwrap()
        .then((row: ICatalogRow | null) => {
          RESOLUTION_CACHE.set(key, row ?? null);
          return row ?? null;
        })
        .catch(() => null)
        .finally(() => {
          RESOLUTION_INFLIGHT.delete(key);
        }) as Promise<ICatalogRow | null>;
      RESOLUTION_INFLIGHT.set(key, promise);
    }
    void promise.then((row) => {
      if (!cancelled) setResolved(row);
    });
    return () => {
      cancelled = true;
    };
  }, [value, rows, mfgId, path, constructionClassId, dispatch]);

  /**
   * The open dropdown is anchored in VIEWPORT space, not inside the data bar.
   *
   * common.css gives `ul.options` `position: absolute` under the control, which
   * puts it inside whichever panel hosts the select — and every one of those is
   * a scroll container (`#forms_sections` is `overflow-y: auto; overflow-x:
   * hidden`, ahu/styles.css:1749; `#forms_ahu` likewise, :1636). An absolutely
   * positioned box still counts towards its scroller's scrollable overflow, so
   * a 200px menu opened on a field in the bottom ~216px of the panel grew the
   * Data Bar's scroll area for exactly as long as the menu was open: the 10px
   * scrollbar appeared, every row reflowed 10px narrower, and the part of the
   * menu below the panel edge was clipped away by the same `overflow` (so the
   * options you had to scroll for were the ones you could not see).
   *
   * `position: fixed` takes it out of the scroller's overflow AND out of its
   * clip — a fixed box is only clipped by an ancestor that is its containing
   * block, which plain `overflow` does not make one — so the panel's geometry
   * no longer changes at all when a select opens.
   *
   * The division by `cssZoom()` is the board's own doing: `body` carries a CSS
   * `zoom` (~0.76, the scale-to-fit on /ahu/detail). `getBoundingClientRect()`
   * reports post-zoom viewport px while the lengths we hand back are re-scaled
   * by that same zoom when painted, so they have to be divided out first. It is
   * read off the cascade rather than inferred from `rect.height ÷ offsetHeight`
   * because `offsetHeight` is rounded to whole px: on an 18px-tall control that
   * rounding is 1.3% of the factor, which lands the menu ~7px above the field.
   */
  useLayoutEffect(() => {
    if (!open) {
      setMenuPos(null);
      return undefined;
    }
    const place = (e?: Event) => {
      const el = rootRef.current;
      // The list is itself a 200px scroller, and a capture-phase listener on
      // window sees its scrolls too. Paging through a long catalogue must not
      // re-measure (and re-render) the menu on every wheel tick — the menu has
      // not moved.
      if (e && listRef.current && e.target === listRef.current) return;
      if (!el) return;
      const r = el.getBoundingClientRect();
      // Measured where the menu LIVES (the portal under <body>), not where the
      // control does — that is the zoom its own lengths get re-scaled by.
      const z = cssZoom(listRef.current ?? el);
      // Painted height of the menu — the real one once it has rendered, the
      // stylesheet's max-height as the estimate for the very first pass.
      const menuH = (listRef.current?.offsetHeight || MENU_MAX_HEIGHT) * z;
      // Flip above the control only when below genuinely cannot hold it and
      // above can: the data bar runs to the bottom of the window, so the last
      // few fields have no room under them.
      const below = window.innerHeight - r.bottom;
      const flip = below < menuH && r.top > below;
      // Being out of the panel's clip is right while the field is on screen and
      // wrong once it is not: scrolling the Data Bar used to carry the menu away
      // with its field, over the tab strip and the header, because nothing
      // bounds a fixed box. Legacy clipped the two together. Hide the menu when
      // its control has left the panel entirely — partial overlap still shows,
      // which is the whole point of escaping the clip.
      const panel = scrollParent(el);
      const pr = panel?.getBoundingClientRect();
      const offPanel = !!pr && (r.bottom <= pr.top || r.top >= pr.bottom);
      const top = (flip ? r.top - menuH : r.bottom) / z;
      const left = r.left / z;
      const width = r.width / z;
      // Keep the previous object when nothing moved: `place` runs on every
      // scroll event in the document, and a fresh object would re-render the
      // whole option list each time.
      setMenuPos((prev) =>
        prev && prev.top === top && prev.left === left && prev.width === width && prev.off === offPanel
          ? prev
          : { top, left, width, off: offPanel }
      );
    };
    place();
    // Capture phase: the data bar scrolls, not the window, so the scroll event
    // never reaches window in the bubble phase.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
    // rows.length: paging in more options makes the menu taller, which can
    // change the side it belongs on.
  }, [open, rows.length]);

  // Per-instance outside-click close ([NEG]#46 non-replication).
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (!target || !rootRef.current) return;
      // The menu is portaled out of the root (see `menu` below), so "inside"
      // now means either box — without the second test a click on the list's
      // own scrollbar or padding would read as an outside click and close it.
      if (rootRef.current.contains(target)) return;
      if (listRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [open]);

  const onScroll = (e: React.UIEvent<HTMLUListElement>) => {
    const el = e.currentTarget;
    if (
      el.scrollHeight - el.scrollTop - el.clientHeight < 10 &&
      rows.length < count &&
      !loadingRef.current
    ) {
      setPage((p) => p + 1);
    }
  };

  const current = rows.find((r) => r.id === value) ?? resolved;
  // Legacy closed-state text: '-----' for null (initial selectedText AND the
  // getValue(null) branch) and '-----' until getValue resolves (R14). The
  // bulk panel overrides the null text with 'Varies' for mixed values.
  const selectedText = value == null ? nullText : catalogLabel(current) || '-----';

  const pick = (id: number | null) => {
    setOpen(false);
    onChange(id);
  };

  /**
   * The menu is portaled to `<body>`, and it has to be.
   *
   * `#ahu-board-content` is `position: relative; z-index: 0` — a stacking
   * context — so every z-index inside the board, including this list's
   * `z-index: 10`, is sealed inside it and the whole board paints as one layer.
   * `#ksdm-footer`'s wrappers are positioned with `z-index: auto`, i.e. the same
   * layer, and the footer comes after `#ksdm-content` in the DOM, so it wins on
   * tree order: the copyright line drew straight through the bottom of the menu
   * (`elementFromPoint` over the last option returned `small#ksdm-copyright`).
   * The footer's own `z-index: -1` (skeleton.css:1596) never applied — it is
   * `position: static`.
   *
   * Out at `<body>` the list is a sibling of `#react-root` in the stacking
   * context `body`'s own `zoom` creates, where its `z-index: 10` finally counts
   * against the footer's `auto`.
   *
   * The wrapper exists only so the legacy stylesheet still reaches the list:
   * all of its chrome comes from the DESCENDANT selector
   * `.custom-select-with-pagination ul.options` (common.css:32293), which a bare
   * portaled `<ul>` would fall out of. `display: contents` means it is a
   * selector-matching ancestor and nothing else — no box, no pseudo-elements, no
   * stray full-width block at the end of `<body>`.
   *
   * React portals keep bubbling through the REACT tree, so the option `onClick`
   * handlers below still fire from out here.
   */
  const menu = createPortal(
    <div className="custom-select-with-pagination" style={{ display: 'contents' }}>
      <ul
        ref={listRef}
        className="options"
        style={
          menuPos
            ? {
                position: 'fixed',
                top: menuPos.top,
                left: menuPos.left,
                width: menuPos.width,
                ...(menuPos.off
                  ? { visibility: 'hidden' as const, pointerEvents: 'none' as const }
                  : null),
              }
            : // One layout pass before `place()` has measured — fixed already,
              // just not anchored yet. useLayoutEffect fills it in before paint.
              { position: 'fixed', visibility: 'hidden' }
        }
        onScroll={onScroll}
      >
        {hasNoneOption && (
          <li data-id="" className="option-li" onClick={() => pick(null)}>
            {' '}
            - -{' '}
          </li>
        )}
        {rows.map((r) => (
          <li
            key={r.id}
            data-id={r.id}
            className={`option-li${r.id === value ? ' selected-li' : ''}`}
            onClick={() => pick(r.id)}
          >
            {catalogLabel(r)}
          </li>
        ))}
      </ul>
    </div>,
    document.body
  );

  return (
    <div
      ref={rootRef}
      className={`custom-select-with-pagination${open ? ' open' : ''}${
        disabled ? ' non-clickable' : ''
      }`}
    >
      {/* Hidden native select — the legacy host-handler / harness contract. */}
      <select
        style={{ display: 'none' }}
        className={selectClass}
        name={selectName}
        value={value == null ? 'None' : String(value)}
        data-id={dataId}
        data-plane={dataPlane}
        onChange={(e) => {
          const v = e.target.value;
          pick(v === 'None' || v === '' ? null : parseInt(v, 10));
        }}
      >
        {hasNoneOption && <option value="None">- -</option>}
        {current && !rows.some((r) => r.id === current.id) && (
          <option value={current.id}>{catalogLabel(current)}</option>
        )}
        {rows.map((r) => (
          <option key={r.id} value={r.id}>
            {catalogLabel(r)}
          </option>
        ))}
      </select>
      <div
        className="selected"
        title={selectedText.length > 28 ? selectedText : undefined}
        onClick={() => {
          if (!disabled) setOpen((o) => !o);
        }}
      >
        {selectedText}
      </div>
      {open && menu}
    </div>
  );
};

export default PaginatedSelect;
